import type { Account, Trip } from "../types";
import { inferCurrencyForDestination } from "./countries";

/**
 * Returns the primary trip associated with an account.
 * Prioritizes active/planned trips over completed/archived ones.
 */
export function getAccountTrip(account: Account, trips?: Trip[]): Trip | undefined {
  if (!trips || trips.length === 0) return undefined;

  // 1. Explicit membership in trip.accountIds
  const directMatches = trips.filter((t) => (t.accountIds || []).includes(account.id));
  if (directMatches.length > 0) {
    const activeOrPlanned = directMatches.find((t) => t.status === "active" || t.status === "planned");
    if (activeOrPlanned) return activeOrPlanned;
    return directMatches[0];
  }

  // 2. Note or name pattern match (e.g. auto-provisioned "Cash wallet for trip: Malaysia 2026")
  const noteMatches = trips.filter((t) => {
    const tripNameLower = t.name.toLowerCase();
    const hasNote = account.notes && account.notes.toLowerCase().includes(`trip: ${tripNameLower}`);
    const hasPrefix = account.name.toLowerCase().startsWith(`${tripNameLower} ·`);
    return Boolean(hasNote || hasPrefix);
  });

  if (noteMatches.length > 0) {
    const activeOrPlanned = noteMatches.find((t) => t.status === "active" || t.status === "planned");
    if (activeOrPlanned) return activeOrPlanned;
    return noteMatches[0];
  }

  return undefined;
}

/**
 * Checks whether an account is a dedicated trip cash wallet.
 */
export function isTripCashWallet(account: Account, trips?: Trip[]): boolean {
  if (account.type.toLowerCase() !== "cash") return false;
  return Boolean(getAccountTrip(account, trips));
}

/**
 * Categorizes an account for the Accounts section:
 * - 'main': Everyday bank/card, everyday cash, or active/planned trip cash wallet
 * - 'leftover': Completed/archived trip cash wallet with remaining balance
 * - 'settled': Completed/archived trip cash wallet with zero balance (or discontinued zero-balance account)
 */
export function classifyAccountForView(
  account: Account,
  trips?: Trip[],
): "main" | "leftover" | "settled" {
  const isCash = account.type.toLowerCase() === "cash";
  const trip = getAccountTrip(account, trips);
  const isTripWallet = isCash && Boolean(trip);
  const isTripClosed = trip ? (trip.status === "completed" || trip.status === "archived") : false;
  const isZero = Math.abs(account.balance) < 0.005;

  if (isTripWallet && isTripClosed) {
    return isZero ? "settled" : "leftover";
  }

  if (account.active === false && isZero) {
    return "settled";
  }

  return "main";
}

/**
 * Returns all active accounts associated with a trip (either via trip.accountIds
 * or by note/name convention matching).
 */
export function getTripAccounts(trip: Trip, accounts: Account[], trips?: Trip[]): Account[] {
  const allTrips = trips && trips.length > 0 ? trips : [trip];
  return accounts.filter((a) => {
    if (a.active === false) return false;
    if ((trip.accountIds || []).includes(a.id)) return true;
    if (getAccountTrip(a, allTrips)?.id === trip.id) return true;
    return false;
  });
}

/**
 * Selects the best default account for a transaction associated with a trip.
 * Prioritizes:
 * - For transfers: USD cash wallet if paired with destination wallet (common FX exchange)
 * - Destination currency match (positive balance first, then any)
 * - Any cash wallet with positive balance
 * - Any account with positive balance
 * - USD cash wallet
 * - Any cash wallet
 * - First linked trip account
 */
export function getDefaultTripAccount(
  trip: Trip,
  accounts: Account[],
  destination?: string,
  trips?: Trip[],
  type?: "Expense" | "Income" | "Transfer",
): Account | undefined {
  const tripAccs = getTripAccounts(trip, accounts, trips);
  if (tripAccs.length === 0) return undefined;

  const targetDest = destination || trip.destinations?.[0];
  const targetCurrency = targetDest ? inferCurrencyForDestination(targetDest) : undefined;

  // For Transfer, if there is a USD wallet and a destination wallet, transferring FROM USD is the common FX pattern
  if (type === "Transfer") {
    const usdWallet = tripAccs.find(
      (a) => a.type.toLowerCase() === "cash" && a.currency === "USD",
    );
    const destWallet = targetCurrency
      ? tripAccs.find((a) => a.currency === targetCurrency && a.id !== usdWallet?.id)
      : undefined;
    if (usdWallet && destWallet) {
      return usdWallet;
    }
  }

  // 1. Destination currency match
  if (targetCurrency) {
    const matchingCurrency = tripAccs.filter((a) => a.currency === targetCurrency);
    if (matchingCurrency.length > 0) {
      const withBalance = matchingCurrency.find((a) => a.balance > 0);
      return withBalance || matchingCurrency[0];
    }
  }

  // 2. Any cash wallet with positive balance
  const cashWithBalance = tripAccs.find(
    (a) => a.type.toLowerCase() === "cash" && a.balance > 0,
  );
  if (cashWithBalance) return cashWithBalance;

  // 3. Any account with positive balance
  const anyWithBalance = tripAccs.find((a) => a.balance > 0);
  if (anyWithBalance) return anyWithBalance;

  // 4. USD cash wallet
  const usdCash = tripAccs.find(
    (a) => a.type.toLowerCase() === "cash" && a.currency === "USD",
  );
  if (usdCash) return usdCash;

  // 5. Any cash wallet
  const anyCash = tripAccs.find((a) => a.type.toLowerCase() === "cash");
  if (anyCash) return anyCash;

  // 6. First trip account
  return tripAccs[0];
}

/**
 * For transfers within a trip, selects the receiving account.
 * Prioritizes:
 * - An account matching the destination currency (other than fromAccountId)
 * - Any other account associated with the trip
 */
export function getDefaultTripToAccount(
  trip: Trip,
  accounts: Account[],
  fromAccountId?: string,
  destination?: string,
  trips?: Trip[],
): Account | undefined {
  const tripAccs = getTripAccounts(trip, accounts, trips);
  if (tripAccs.length === 0) return undefined;

  const targetDest = destination || trip.destinations?.[0];
  const targetCurrency = targetDest ? inferCurrencyForDestination(targetDest) : undefined;

  // 1. Destination currency match
  if (targetCurrency) {
    const destMatch = tripAccs.find(
      (a) => a.id !== fromAccountId && a.currency === targetCurrency,
    );
    if (destMatch) return destMatch;
  }

  // 2. Any other account linked to the trip
  const another = tripAccs.find((a) => a.id !== fromAccountId);
  if (another) return another;

  return undefined;
}

export interface TripPacing {
  totalDays: number;
  daysElapsed: number;
  daysRemaining: number;
  currentDay: number;
  stage: "upcoming" | "active" | "completed";
  daysUntilStart: number;
  dailyAverageSpend: number;
  plannedDailyBudget: number | null;
  dailyBudgetRemaining: number | null;
  isPacingOver: boolean;
}

function parseISODateDays(dateStr: string): number {
  const [y, m, d] = dateStr.slice(0, 10).split("-").map(Number);
  return Math.floor(Date.UTC(y, m - 1, d) / (1000 * 60 * 60 * 24));
}

/**
 * Calculates pacing and daily burn statistics for a trip across its lifecycle stages.
 */
export function calculateTripPacing(
  startDate: string,
  endDate: string,
  today: string,
  totalSpendBudget: number,
  budget: number | null,
  status?: string,
): TripPacing {
  const startDays = parseISODateDays(startDate);
  const endDays = parseISODateDays(endDate);
  const todayDays = parseISODateDays(today);

  const totalDays = Math.max(1, endDays - startDays + 1);

  let stage: "upcoming" | "active" | "completed";
  if (status === "completed" || status === "archived") {
    stage = "completed";
  } else if (todayDays < startDays) {
    stage = "upcoming";
  } else if (todayDays > endDays) {
    stage = "completed";
  } else {
    stage = "active";
  }

  let daysElapsed = 0;
  let daysRemaining = 0;
  let currentDay = 0;
  let daysUntilStart = 0;

  if (stage === "upcoming") {
    daysUntilStart = Math.max(1, startDays - todayDays);
    daysElapsed = 0;
    currentDay = 0;
    daysRemaining = totalDays;
  } else if (stage === "active") {
    daysElapsed = Math.min(totalDays, Math.max(1, todayDays - startDays + 1));
    currentDay = daysElapsed;
    daysRemaining = Math.max(1, endDays - todayDays + 1);
  } else {
    daysElapsed = totalDays;
    currentDay = totalDays;
    daysRemaining = 0;
  }

  const plannedDailyBudget = budget !== null && budget > 0 ? budget / totalDays : null;

  let dailyAverageSpend = 0;
  if (stage === "completed") {
    dailyAverageSpend = totalDays > 0 ? totalSpendBudget / totalDays : 0;
  } else if (stage === "active") {
    dailyAverageSpend = daysElapsed > 0 ? totalSpendBudget / daysElapsed : 0;
  } else {
    dailyAverageSpend = totalSpendBudget > 0 ? totalSpendBudget / totalDays : 0;
  }

  const remainingBudget = budget !== null ? budget - totalSpendBudget : null;
  let dailyBudgetRemaining: number | null = null;
  if (remainingBudget !== null) {
    if (stage === "active") {
      dailyBudgetRemaining = remainingBudget > 0 && daysRemaining > 0 ? remainingBudget / daysRemaining : 0;
    } else if (stage === "upcoming") {
      dailyBudgetRemaining = remainingBudget > 0 ? remainingBudget / totalDays : 0;
    } else {
      dailyBudgetRemaining = 0;
    }
  }

  const isPacingOver =
    plannedDailyBudget !== null &&
    (dailyAverageSpend > plannedDailyBudget || (remainingBudget !== null && remainingBudget < 0));

  return {
    totalDays,
    daysElapsed,
    daysRemaining,
    currentDay,
    stage,
    daysUntilStart,
    dailyAverageSpend,
    plannedDailyBudget,
    dailyBudgetRemaining,
    isPacingOver,
  };
}

/**
 * Strips redundant trip name prefixes when viewing accounts inside a trip context.
 * e.g. "Malaysia and Indonesia trip · IDR Cash" -> "IDR Cash"
 */
export function cleanTripAccountName(accountName: string, tripName?: string): string {
  if (!accountName) return "";
  let name = accountName;
  if (tripName) {
    const escaped = tripName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const stripped = name.replace(new RegExp(`^${escaped}\\s*[-·•–—:]\\s*`, "i"), "");
    if (stripped !== name) return stripped.trim() || accountName;
  }
  // Generic fallback if still contains " · " before a currency code / wallet keyword
  name = name.replace(/^.+?\s*[-·•–—:]\s*(?=[A-Z]{3}\b|Cash\b|Wallet\b)/i, "");
  return name.trim() || accountName;
}

